import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ORG } from "@/lib/demo-data";
import { MISSION_HOME, missionAuthCallbackURL, parseAuthError } from "@/lib/auth-callback";
import { type AuthHealth } from "@/lib/auth-health";
import { cn } from "@/lib/utils";
import { Github } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

export const MISSION_PHOTO = "/mission-launch.jpg";
export { MISSION_HOME };

export function MissionPhotoStage({
  veil,
  contained = false,
}: {
  veil: "landing" | "auth";
  /** Hero-only plate. Auth stays `fixed` so the still covers the viewport. */
  contained?: boolean;
}) {
  return (
    <div
      className={cn(
        "mission-photo-stage pointer-events-none overflow-hidden bg-black",
        contained ? "absolute inset-0" : "fixed inset-0",
      )}
      aria-hidden
    >
      <img
        src={MISSION_PHOTO}
        alt=""
        width={1536}
        height={864}
        fetchPriority="high"
        decoding="sync"
        className="mission-photo-img"
      />
      <div className={cn("absolute inset-0", veil === "landing" ? "mission-veil-landing" : "mission-veil-auth")} />
    </div>
  );
}

export function MissionMark({ size = 24 }: { size?: number }) {
  return (
    <span
      className="grid place-items-center rounded-[2px] border border-white/30 font-mono text-[9px] font-bold text-white"
      style={{ width: size, height: size }}
    >
      RS
    </span>
  );
}

export function MissionBrand({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-2.5 text-[15px] font-semibold tracking-[0.06em] text-white uppercase", className)}>
      <MissionMark />
      PCBX
    </div>
  );
}

export function AuthGlassCard({ children }: { children: ReactNode }) {
  return (
    <div className="relative z-[2] grid min-h-full place-items-center overflow-auto p-6">
      <div className="w-full max-w-[400px] max-h-[calc(100dvh-3rem)] overflow-y-auto overscroll-contain rounded-[4px] border border-white/14 bg-[rgba(10,10,10,0.84)] px-6 py-5 shadow-[0_24px_80px_rgba(0,0,0,0.55)] backdrop-blur-[18px]">
        {children}
      </div>
    </div>
  );
}

const pill =
  "inline-flex h-10 w-full items-center justify-center gap-2 rounded-full text-[13px] font-semibold tracking-[0.04em]";

/** Classic 4-color Google G — used only on the Google auth button. */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={18}
      height={18}
      aria-hidden
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function AuthField({
  id,
  label,
  type = "text",
  placeholder,
  autoComplete,
  defaultValue,
  disabled,
}: {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  defaultValue?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-[10px] font-semibold tracking-[0.1em] text-white/50 uppercase">
        {label}
      </Label>
      <Input
        id={id}
        name={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        disabled={disabled}
        className="h-10 rounded-[4px] border-white/16 bg-black/45 text-[14px] text-white placeholder:text-white/35"
      />
    </div>
  );
}

export function SocialButtons({
  health,
  busy,
  onGithub,
  onGoogle,
  githubSolid = true,
}: {
  health: AuthHealth;
  busy: boolean;
  onGithub: () => void;
  onGoogle: () => void;
  githubSolid?: boolean;
}) {
  const betterAuth = health.auth_mode === "better_auth";
  const githubOk = betterAuth && Boolean(health.github_oauth);
  const googleOk = betterAuth && Boolean(health.google_oauth);
  return (
    <div className="flex flex-col gap-2.5">
      <Button
        className={pill}
        variant={githubSolid ? "default" : "outline"}
        disabled={busy || !githubOk}
        onClick={onGithub}
        type="button"
      >
        <Github className="size-4 shrink-0 text-current" aria-hidden />
        Continue with GitHub
      </Button>
      <Button className={pill} variant="outline" disabled={busy || !googleOk} onClick={onGoogle} type="button">
        <GoogleMark className="size-[18px] shrink-0" />
        Continue with Google
      </Button>
    </div>
  );
}

export function AuthOr() {
  return (
    <div className="my-3 flex items-center gap-3 text-[11px] tracking-[0.1em] text-white/40 uppercase">
      <span className="h-px flex-1 bg-white/12" />
      or
      <span className="h-px flex-1 bg-white/12" />
    </div>
  );
}

function authDetailLines(health: AuthHealth, loaded: boolean): string[] {
  if (!loaded) return [];
  const lines: string[] = [];
  const betterAuth = health.auth_mode === "better_auth";
  const cookieDemo = health.auth_mode === "cookie_demo";
  const authBypass = health.auth_mode === "auth_bypass";
  const githubOk = betterAuth && Boolean(health.github_oauth);
  const googleOk = betterAuth && Boolean(health.google_oauth);
  const emailOk = betterAuth && Boolean(health.email_auth);
  const staging = health.environment === "staging";

  if (!health.auth_mode) {
    lines.push(
      "Auth health unavailable. Sign-in is refused until the worker reports a bound mode (fail-closed, not a stub).",
    );
    return lines;
  }
  if (authBypass) {
    lines.push(
      staging
        ? "Staging — auth bypass / no login. Not Better Auth. Not production SSO. Not pcbx.ai."
        : "Local — auth bypass / no login. Not Better Auth. Not production SSO.",
    );
    return lines;
  }
  if (betterAuth) {
    lines.push(
      staging
        ? "Staging workers.dev — Better Auth. GitHub / Google optional. Email needs AUTH_DB. Not pcbx.ai production."
        : "Better Auth — GitHub, Google, email. Cookie demo is off while AUTH is bound.",
    );
  } else if (cookieDemo) {
    lines.push(
      staging
        ? `STAGING only. Cookie demo lands on org ${ORG.slug}. Not pcbx.ai production.`
        : `LOCAL only. Cookie demo lands on org ${ORG.slug}. Password is not a user table. Not pcbx.ai production.`,
    );
  } else {
    lines.push("Auth is unbound. Sign-in is refused (not a stub SSO). Bind AUTH_DB + BETTER_AUTH_SECRET.");
  }
  if (!githubOk) {
    lines.push(
      "GitHub OAuth is not bound. GITHUB_CLIENT_ID / GITHUB_CLIENT_SECRET missing — this button will not invent a session.",
    );
  }
  if (!googleOk) {
    lines.push(
      "Google OAuth is not bound. GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET missing — this button will not invent a session.",
    );
  }
  if (!emailOk && !cookieDemo) {
    lines.push("Email auth needs AUTH_DB + BETTER_AUTH_SECRET. This form will not invent a session.");
  }
  return lines;
}

export function StagingNote({ health, loaded = true }: { health: AuthHealth; loaded?: boolean }) {
  const envLabel =
    health.environment === "staging" ? "Staging" : health.environment === "development" ? "Local" : null;
  const brand = envLabel ? `${envLabel} · pcbx.ai · rscircuit` : "pcbx.ai · rscircuit";
  const details = authDetailLines(health, loaded);
  return (
    <div className="mt-3 border-t border-white/10 pt-2.5 text-center text-[11px] text-white/40">
      <p className="m-0 tracking-[0.04em]">{brand}</p>
      {details.length ? (
        <details className="mission-auth-details mt-1.5">
          <summary className="cursor-pointer text-white/45">Details</summary>
          <div className="mt-2 space-y-1.5 text-left text-[11px] leading-snug text-white/38">
            {details.map((line) => (
              <p key={line} className="m-0">
                {line}
              </p>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

export function useMissionAuth(health: AuthHealth) {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const betterAuth = health.auth_mode === "better_auth";
  const cookieDemo = health.auth_mode === "cookie_demo";
  const emailOk = betterAuth && Boolean(health.email_auth);
  const githubOk = betterAuth && Boolean(health.github_oauth);
  const googleOk = betterAuth && Boolean(health.google_oauth);

  async function signInSocial(provider: "github" | "google") {
    const configured = provider === "github" ? githubOk : googleOk;
    if (!configured) {
      setError(
        provider === "github"
          ? "GitHub OAuth secrets are not bound. This button will not invent a session."
          : "Google OAuth secrets are not bound. This button will not invent a session.",
      );
      return;
    }
    setError(null);
    setBusy(true);
    const callbackURL = missionAuthCallbackURL(MISSION_HOME);
    try {
      const res = await fetch("/api/auth/sign-in/social", {
        method: "POST",
        credentials: "include",
        redirect: "manual",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ provider, callbackURL }),
      });
      const location = res.headers.get("location");
      if (location && (res.status === 301 || res.status === 302 || res.status === 303 || res.status === 307 || res.status === 308)) {
        window.location.assign(location);
        return;
      }
      if (res.status === 0 || res.type === "opaqueredirect") {
        setError(`${provider === "github" ? "GitHub" : "Google"} sign-in did not return a redirect URL.`);
        return;
      }
      const data = (await res.json().catch(() => null)) as { url?: string } | null;
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      setError(
        parseAuthError(
          data,
          `${provider === "github" ? "GitHub" : "Google"} sign-in failed (${res.status})`,
          health.app_origin,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitEmail(kind: "sign-in" | "sign-up", fields: { email: string; password: string; name?: string }) {
    if (!emailOk && !cookieDemo) {
      setError("Email auth needs AUTH_DB + BETTER_AUTH_SECRET. This form will not invent a session.");
      return false;
    }
    if (!fields.email || fields.password.length < 8) {
      setError("Email and password (min 8 characters) required.");
      return false;
    }
    setError(null);
    setBusy(true);
    const path = kind === "sign-up" ? "/api/auth/sign-up/email" : "/api/auth/sign-in/email";
    try {
      const res = await fetch(path, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({
          email: fields.email,
          password: fields.password,
          name: fields.name || fields.email.split("@")[0],
          callbackURL: missionAuthCallbackURL(MISSION_HOME),
        }),
      });
      if (cookieDemo && !betterAuth) {
        if (!res.ok) {
          setError(
            parseAuthError(await res.json().catch(() => null), `Sign-in failed (${res.status})`, health.app_origin),
          );
          return false;
        }
        nav(MISSION_HOME);
        return true;
      }
      if (!res.ok) {
        setError(
          parseAuthError(await res.json().catch(() => null), `Auth failed (${res.status})`, health.app_origin),
        );
        return false;
      }
      nav(MISSION_HOME);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function onEmailForm(kind: "sign-in" | "sign-up") {
    return (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form = event.currentTarget;
      const email = (form.elements.namedItem("email") as HTMLInputElement | null)?.value?.trim() || "";
      const password = (form.elements.namedItem("password") as HTMLInputElement | null)?.value || "";
      const name = (form.elements.namedItem("name") as HTMLInputElement | null)?.value?.trim();
      const confirm = (form.elements.namedItem("confirm") as HTMLInputElement | null)?.value;
      if (kind === "sign-up" && confirm !== undefined && confirm !== password) {
        setError("Passwords do not match.");
        return;
      }
      void submitEmail(kind, { email, password, name });
    };
  }

  return { error, busy, setError, signInSocial, onEmailForm, emailOk, cookieDemo, betterAuth };
}

export function AuthFoot({ to, prompt, action }: { to: string; prompt: string; action: string }) {
  return (
    <p className="mt-3 text-center text-[13px] text-white/55">
      {prompt}{" "}
      <Link to={to} className="font-semibold text-falcon no-underline">
        {action}
      </Link>
      {" · "}
      <Link to="/privacy" rel="privacy-policy" className="text-white/55 no-underline hover:text-white">
        Privacy
      </Link>
    </p>
  );
}
